import type { DropGenHourBucket } from '../hour-profile.types';
import type { DropGenRule } from '../rules/rules.types';

export interface SlotReduction {
  count: number;
  minRemaining: number;
}

export interface HourReduction extends SlotReduction {
  hour: number;
  ruleId: string;
  name: string;
}

export interface ReducibleSlot {
  slotId: string;
  minute: number;
  protected?: boolean;
}

export interface HourReductionResult {
  hour: number;
  hourStartMin: number;
  before: number;
  after: number;
  target: number;
  ruleNames: string[];
  removedSlotIds: string[];
  removedMinutes: number[];
}

/** The resolved actions carry parameters from the saved rule, never from display text. */
export function reducedHours(buckets: readonly Pick<DropGenHourBucket, 'hour' | 'ruleHits'>[], rules: readonly DropGenRule[] = []): HourReduction[] {
  const byId = new Map(rules.map((r) => [r.id, r]));
  return buckets.flatMap((b) => {
    const fired = (b.ruleHits ?? []).filter((h) => h.state === 'fired');
    if (fired.some((h) => h.action === 'block' && !byId.get(h.ruleId)?.scope)) return [];
    return fired.flatMap((h) => {
      const rule = byId.get(h.ruleId);
      return h.action === 'reduce_slots' && rule?.enabled && rule.reduceSlots
        ? [{ hour: b.hour, ruleId: rule.id, name: rule.name, ...rule.reduceSlots }]
        : [];
    });
  });
}

/** Pure, repeatable: reduce the original combined schedule once, never reflow or refill. */
export function reduceHourSlots(slots: readonly ReducibleSlot[], reductions: readonly HourReduction[]): HourReductionResult[] {
  const byHour = new Map<number, ReducibleSlot[]>();
  for (const slot of slots) {
    const start = Math.floor(slot.minute / 60) * 60;
    byHour.set(start, [...(byHour.get(start) ?? []), slot]);
  }
  const out: HourReductionResult[] = [];
  for (const [start, original] of byHour) {
    const hour = ((start / 60) % 24 + 24) % 24;
    const matches = reductions.filter((r) => r.hour === hour);
    if (matches.length === 0) continue;
    const n = original.length;
    const targetOf = (r: SlotReduction) => Math.min(n, Math.max(r.minRemaining, n - r.count));
    const target = Math.min(...matches.map(targetOf));
    const removed = original.map((slot, index) => ({ slot, index }))
      .filter(({ slot }) => !slot.protected)
      .sort((a, b) => b.slot.minute - a.slot.minute || b.index - a.index)
      .slice(0, n - target).map(({ slot }) => slot);
    out.push({
      hour, hourStartMin: start, before: n, after: n - removed.length, target,
      ruleNames: matches.filter((r) => targetOf(r) === target).map((r) => r.name),
      removedSlotIds: removed.map((s) => s.slotId),
      removedMinutes: removed.map((s) => s.minute),
    });
  }
  return out;
}

export const reductionWords = (r: HourReductionResult): string => {
  const time = (minute: number) => `${String(Math.floor(((minute % 1440) + 1440) % 1440 / 60)).padStart(2, '0')}:${String(((minute % 60) + 60) % 60).padStart(2, '0')}`;
  return `${time(r.hourStartMin)}: ${r.before} → ${r.after} slots — ${r.ruleNames.join(', ')}${r.removedMinutes.length ? `; removed ${r.removedMinutes.map(time).join(', ')}` : '; no eligible slots removed'}${r.after > r.target ? '; sent, in-flight or protected slots kept' : ''}`;
};
